import React, { useMemo } from "react";

import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Label,
} from "recharts";

import { SalesDataResponse } from "../../api/PartnerPortalGatewayApi";
import { normalizeNumber } from "../../utils/formattingFunctions";
import { createCustomLegendRenderer } from "./customLegendRenderer";
import { lineChartIcon } from "./lineChartIcon";

interface OutsideSchoolHoursCareChartProps {
  data: SalesDataResponse[];
  isLoading?: boolean;
  title?: string;
  height?: number;
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
}

// Colors for different care types matching attendance trends requirements
const getCareTypeColor = (careType: string): string => {
  const colors: Record<string, string> = {
    BSC: "#00DDFF", // Before School Care - Cyan
    ASC: "#D08DFF", // After School Care - Magenta
    VAC: "#5CE5BB", // Vacation Care - Light teal/green (fallback)
  };
  return colors[careType] || "#5CE5BB";
};

export const OutsideSchoolHoursCareChart: React.FC<OutsideSchoolHoursCareChartProps> = ({
  data,
  isLoading = false,
  title = "Your OSHC Attendance",
  height = 300,
  startDate,
  endDate,
}) => {
  // Memoize chart data for Recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Filter to only include outside school hours care (BSC, ASC - exclude VAC)
    const filteredData = data.filter((item) => item.code && item.code !== "VAC");

    if (filteredData.length === 0) {
      return [];
    }

    // Group by weekBegin and care type, then calculate weekly averages
    const weeklyGroups = filteredData.reduce(
      (acc, item) => {
        if (!item.weekBegin || !item.code) return acc;

        const weekKey = item.weekBegin.format("YYYY-MM-DD"); // Use weekBegin as key
        const careType = item.code;

        if (!acc[weekKey]) {
          acc[weekKey] = {
            weekBegin: item.weekBegin,
            careTypes: {},
          };
        }

        if (!acc[weekKey].careTypes[careType]) {
          acc[weekKey].careTypes[careType] = [];
        }

        acc[weekKey].careTypes[careType].push(item.numBk || 0);
        return acc;
      },
      {} as Record<string, { weekBegin: dayjs.Dayjs; careTypes: Record<string, number[]> }>,
    );

    // Get date range for filtering (optional, since API should already filter)
    const dataStartDate = startDate || dayjs().subtract(30, "days");
    const dataEndDate = endDate || dayjs();

    // Get all unique week starts from the actual data and filter by date range
    const actualWeeks = Object.values(weeklyGroups)
      .map((group) => group.weekBegin)
      .filter((weekStart) => {
        return (
          (weekStart.isAfter(dataStartDate) || weekStart.isSame(dataStartDate, "day")) &&
          (weekStart.isBefore(dataEndDate) || weekStart.isSame(dataEndDate, "day"))
        );
      })
      .sort((a, b) => a.valueOf() - b.valueOf());

    // If we have actual weeks but want to fill gaps, create complete week range
    if (actualWeeks.length > 0) {
      const firstWeek = actualWeeks[0];
      const lastWeek = actualWeeks[actualWeeks.length - 1];

      const allWeeks: dayjs.Dayjs[] = [];
      let currentWeek = firstWeek;

      while (currentWeek.isBefore(lastWeek) || currentWeek.isSame(lastWeek)) {
        allWeeks.push(currentWeek);
        currentWeek = currentWeek.add(1, "week");
      }

      // Get all care types that appear in the data
      const allCareTypes = new Set<string>();
      filteredData.forEach((item) => {
        if (item.code && item.code !== "VAC") {
          allCareTypes.add(item.code);
        }
      });

      // Convert to chart format: create entry for every week, fill missing weeks with 0
      const chartResult = allWeeks.map((weekStart) => {
        const weekKey = weekStart.format("YYYY-MM-DD");
        const chartPoint: any = {
          week: weekStart.format("DD-MMM-YYYY"), // Week starting date for display (DD-MMM-YYYY format)
          weekFull: weekStart.format("YYYY-MM-DD"), // Full date for tooltip
        };

        // Initialize all care types to 0 for this week
        allCareTypes.forEach((careType) => {
          chartPoint[careType] = 0;
        });

        // If we have data for this week, calculate averages
        if (weeklyGroups[weekKey]) {
          Object.entries(weeklyGroups[weekKey].careTypes).forEach(([careType, values]) => {
            const average =
              values.length > 0 ? Number((values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(2)) : 0;
            chartPoint[careType] = average;
          });
        }

        return chartPoint;
      });

      return chartResult;
    }

    // Fallback: if no weeks in range, return empty
    return [];
  }, [data, startDate, endDate]);

  // Get unique care types for legend (excluding VAC)
  const careTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((item) => {
      if (item.code && item.code !== "VAC") {
        types.add(item.code);
      }
    });
    return Array.from(types);
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Alert severity="info">No your OSHC attendance data available for this service</Alert>
      </Box>
    );
  }

  if (careTypes.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Alert severity="info">No your OSHC care data (only vacation care available)</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 4,
            height: 20,
            backgroundColor: "#5CE5BB",
            borderRadius: 1,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#2c3e50",
            fontFamily: "ABC Whyte",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "#6c757d",
          mb: 3,
          fontFamily: "ABC Whyte",
        }}
      >
        Weekly attendance averages for your OSHC programs
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="weekFull"
            tickFormatter={(value) => {
              return dayjs(value).format("DD-MMM");
            }}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#6c757d", fontSize: 16 }}
            angle={-45}
            textAnchor="end"
            height={80}
            tickMargin={30}
          />

          {/* <YAxis axisLine={false} tickLine={false} tick={{ fill: "#6c757d", fontSize: 16 }} /> */}

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={false}
            label={{ value: "Attendance count", angle: -90, fill: "#6c757d", fontSize: 16 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#ffffff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            formatter={((value: number | undefined, name: string | undefined) => [
              (value ?? 0).toFixed(2),
              name === "BSC" ? "Before School Care" : name === "ASC" ? "After School Care" : name,
            ]) as any}
            labelFormatter={(label: React.ReactNode) => {
              if (!label) return "Week";
              const labelStr = String(label);
              const weekData = chartData.find((item) => item.week === labelStr);
              return weekData ? `Week of ${weekData.weekFull}` : `Week of ${labelStr}`;
            }}
          />
          <Legend
            content={createCustomLegendRenderer({
              position: "bottom",
              wrapperStyles: {
                paddingTop: "20px",
              },
              itemStyles: {
                fontSize: "16px", // Changed from 12px to 16px for mobile accessibility
              },
              iconRenderer: lineChartIcon,
            })}
          />
          {careTypes.map((careType) => (
            <Line
              key={careType}
              type="monotone"
              dataKey={careType}
              stroke={getCareTypeColor(careType)}
              strokeWidth={2}
              dot={{ fill: getCareTypeColor(careType), strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: getCareTypeColor(careType), strokeWidth: 2 }}
              connectNulls={false}
              name={careType === "BSC" ? "Before School Care" : careType === "ASC" ? "After School Care" : careType}
            >
              <LabelList
                dataKey={careType}
                position={{ x: 2, y: -10 }}
                formatter={(value) => (value ? normalizeNumber(Number(value), 0) : "")}
                fontSize={18}
              />
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
